import { useEffect, useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { ArrowRight, ArrowLeft, ShieldAlert, Building2, Send, CheckCircle2, Workflow, ShieldCheck, LayoutGrid, TrendingUp } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const FLOWQA_LOGO = "/brand/flowqa-logo-simplified.png";

const HERO_FEATURES = [
  {
    key: "flow",
    eyebrow: "FLOW",
    title: "Streamlined Workflows",
    icon: Workflow,
    iconBg: "bg-sky-500/15",
    iconRing: "ring-sky-400/30",
    iconColor: "text-sky-300",
  },
  {
    key: "quality",
    eyebrow: "QUALITY",
    title: "Built-in Assurance and Compliance",
    icon: ShieldCheck,
    iconBg: "bg-teal-500/15",
    iconRing: "ring-teal-400/30",
    iconColor: "text-teal-300",
  },
  {
    key: "structure",
    eyebrow: "STRUCTURE",
    title: "Organized Data. Stronger Projects.",
    icon: LayoutGrid,
    iconBg: "bg-slate-500/20",
    iconRing: "ring-slate-300/30",
    iconColor: "text-slate-200",
  },
  {
    key: "progress",
    eyebrow: "PROGRESS",
    title: "Real-time Visibility. Better Outcomes.",
    icon: TrendingUp,
    iconBg: "bg-emerald-500/15",
    iconRing: "ring-emerald-400/30",
    iconColor: "text-emerald-300",
  },
];

export default function Login() {
  const { user, login } = useAuth();
  const nav = useNavigate();
  const [step, setStep] = useState("email"); // email | password | known_no_user | unknown_domain | submitted
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [companyHint, setCompanyHint] = useState(null);
  const [bannerMessage, setBannerMessage] = useState("");
  const [branding, setBranding] = useState(null);
  const [reqForm, setReqForm] = useState({ full_name: "", company_name: "", phone: "", role_title: "", message: "" });

  useEffect(() => {
    axios.get(`${API}/public/organisation/branding`).then(r => setBranding(r.data)).catch(() => {});
  }, []);

  if (user) return <Navigate to="/" replace />;

  const lookup = async () => {
    if (!email.trim()) { toast.error("Enter your work email"); return; }
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/auth/email-lookup`, { email: email.trim() });
      if (data.status === "user_found") {
        setCompanyHint({ name: data.company_name, logo: data.company_logo_url, userName: data.name });
        setStep("password");
      } else if (data.status === "domain_known_no_user") {
        setBannerMessage(data.message);
        setStep("known_no_user");
      } else if (data.status === "domain_unknown") {
        setBannerMessage(data.message);
        setReqForm((f) => ({ ...f, company_name: f.company_name }));
        setStep("unknown_domain");
      } else if (data.status === "personal_email") {
        toast.error(data.message);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Lookup failed");
    } finally { setSubmitting(false); }
  };

  const doLogin = async () => {
    if (!password) { toast.error("Enter your password"); return; }
    setSubmitting(true);
    try {
      await login(email.trim(), password);
      nav("/");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Invalid email or password");
    } finally { setSubmitting(false); }
  };

  const submitAccessRequest = async () => {
    if (!reqForm.full_name || !reqForm.company_name) { toast.error("Name and company name are required"); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/auth/access-request`, {
        ...reqForm,
        email,
        request_type: step === "known_no_user" ? "access_request" : "demo_request",
      });
      setStep("submitted");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally { setSubmitting(false); }
  };

  return (
    <div className="grid min-h-screen grid-cols-1 lg:grid-cols-5">
      {/* Hero panel — deep moss / forest green palette */}
      <div
        className="relative col-span-2 hidden overflow-hidden lg:block"
        style={{
          background:
            "radial-gradient(circle at 18% 22%, rgba(63, 90, 66, 0.35), transparent 55%)," +
            "radial-gradient(circle at 82% 78%, rgba(38, 61, 43, 0.28), transparent 55%)," +
            "linear-gradient(180deg, #263D2B 0%, #17261C 100%)",
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-black/25" />
        <div className="relative z-10 flex h-full flex-col justify-between p-10 text-white">
          {/* Brand badge: full-color FlowQA logo inside a clean white pill */}
          <div className="inline-flex w-fit items-center rounded-2xl bg-white px-5 py-3 shadow-[0_6px_24px_-8px_rgba(0,0,0,0.35),0_2px_6px_-2px_rgba(0,0,0,0.18)] ring-1 ring-black/5" data-testid="login-hero-logo-pill">
            <img src={FLOWQA_LOGO} alt="FlowQA — Build better. Deliver quality." className="h-12 w-auto object-contain sm:h-14 xl:h-16" data-testid="login-hero-logo" />
          </div>
          <div>
            <div className="overline mb-2 text-emerald-200/80">FlowQA · Civil construction QA</div>
            <h1
              className="font-heading text-4xl font-black uppercase leading-[1.05] tracking-tight sm:text-5xl lg:text-6xl"
              data-testid="login-hero-heading"
            >
              BUILD BETTER. DELIVER QUALITY.
            </h1>
          </div>
          <div className="grid grid-cols-2 gap-3 xl:grid-cols-4" data-testid="hero-feature-blocks">
            {HERO_FEATURES.map((f) => (
              <div
                key={f.key}
                data-testid={`hero-feature-${f.key}`}
                className="rounded-xl border border-white/10 bg-white/[0.035] p-4 transition-colors hover:bg-white/[0.07]"
              >
                <div className={`mb-3 grid size-10 place-items-center rounded-lg ring-1 ${f.iconBg} ${f.iconRing}`}>
                  <f.icon className={`size-5 ${f.iconColor}`} />
                </div>
                <div className={`overline text-[10px] tracking-[0.2em] ${f.iconColor}`}>{f.eyebrow}</div>
                <div className="mt-1 text-sm font-semibold leading-snug text-white/95">{f.title}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Right column */}
      <div className="col-span-3 flex items-center justify-center bg-slate-50 p-4 sm:p-10">
        <Card className="w-full max-w-md border-slate-200 p-6 sm:p-8 shadow-sm">
          <img src={FLOWQA_LOGO} alt="FlowQA" className="lg:hidden mb-6 h-10 w-auto object-contain" />

          {step === "email" && (
            <div data-testid="login-step-email">
              <div className="overline mb-1 text-teal-700">Sign in to FlowQA</div>
              <h2 className="font-heading text-2xl font-black tracking-tighter text-slate-900 sm:text-3xl">Enter your work email</h2>
              <p className="mt-1 text-sm text-slate-600">We&apos;ll use your company email to find your FlowQA workspace.</p>
              <div className="mt-5 space-y-3">
                <div>
                  <Label>Work email</Label>
                  <Input data-testid="login-email-input" type="email" placeholder="name@company.com" value={email} onChange={(e) => setEmail(e.target.value)} onKeyDown={(e) => e.key === "Enter" && lookup()} className="h-11" autoFocus />
                </div>
                <Button onClick={lookup} disabled={submitting || !email} className="h-11 w-full bg-teal-700 hover:bg-teal-800" data-testid="login-continue-btn">
                  {submitting ? "Checking…" : "Continue"}
                  <ArrowRight className="ml-2 size-4" />
                </Button>
              </div>
            </div>
          )}

          {step === "password" && (
            <div data-testid="login-step-password">
              <button type="button" onClick={() => { setStep("email"); setPassword(""); setCompanyHint(null); }} className="mb-4 inline-flex items-center text-xs font-semibold text-slate-500 hover:text-slate-800" data-testid="login-back-btn">
                <ArrowLeft className="mr-1 size-3.5" />Use a different email
              </button>
              <div className="flex items-center gap-3">
                {companyHint?.logo
                  ? <img src={companyHint.logo} alt={companyHint?.name || "Company"} className="h-10 w-10 rounded-md border border-slate-200 bg-white object-contain p-1" />
                  : <div className="grid size-10 place-items-center rounded-md bg-teal-50 text-teal-700"><Building2 className="size-5" /></div>}
                <div>
                  <div className="overline text-teal-700">Welcome back{companyHint?.userName ? `, ${companyHint.userName.split(' ')[0]}` : ""}</div>
                  <h2 className="font-heading text-xl font-black tracking-tighter text-slate-900">{companyHint?.name || branding?.company_name || "FlowQA workspace"}</h2>
                </div>
              </div>
              <p className="mt-1 text-xs text-slate-500">Powered by FlowQA</p>
              <div className="mt-5 space-y-3">
                <div>
                  <Label>Email</Label>
                  <Input value={email} disabled className="h-11 bg-slate-50" />
                </div>
                <div>
                  <Label>Password</Label>
                  <Input data-testid="login-password-input" type="password" autoFocus value={password} onChange={(e) => setPassword(e.target.value)} onKeyDown={(e) => e.key === "Enter" && doLogin()} className="h-11" />
                </div>
                <Button onClick={doLogin} disabled={submitting || !password} className="h-11 w-full bg-teal-700 hover:bg-teal-800" data-testid="login-submit-btn">
                  {submitting ? "Signing in…" : "Sign in to FlowQA"}
                  <ArrowRight className="ml-2 size-4" />
                </Button>
                <div className="text-center">
                  <a href="/forgot-password" className="text-xs font-semibold text-teal-700 underline-offset-2 hover:underline" data-testid="login-forgot-password-link">
                    Forgot password?
                  </a>
                </div>
              </div>
            </div>
          )}

          {step === "known_no_user" && (
            <div data-testid="login-step-known-no-user">
              <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
                <ShieldAlert className="mt-0.5 size-4" />
                <span>{bannerMessage}</span>
              </div>
              <h3 className="mt-5 font-heading text-lg font-bold">Request access</h3>
              <p className="mt-1 text-xs text-slate-600">Tell us who you are so your project admin or QA Manager can grant you access.</p>
              <div className="mt-3 space-y-3">
                <div><Label>Full name *</Label><Input value={reqForm.full_name} onChange={(e) => setReqForm({...reqForm, full_name: e.target.value})} data-testid="access-fullname"/></div>
                <div><Label>Company *</Label><Input value={reqForm.company_name} onChange={(e) => setReqForm({...reqForm, company_name: e.target.value})} data-testid="access-company"/></div>
                <div><Label>Role / Title</Label><Input value={reqForm.role_title} onChange={(e) => setReqForm({...reqForm, role_title: e.target.value})} data-testid="access-role"/></div>
                <div><Label>Phone (optional)</Label><Input value={reqForm.phone} onChange={(e) => setReqForm({...reqForm, phone: e.target.value})} data-testid="access-phone"/></div>
                <div><Label>Message (optional)</Label><Input value={reqForm.message} onChange={(e) => setReqForm({...reqForm, message: e.target.value})} placeholder="Project or context" data-testid="access-message"/></div>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => setStep("email")} className="h-11"><ArrowLeft className="mr-1 size-3.5"/>Back</Button>
                  <Button onClick={submitAccessRequest} disabled={submitting} className="h-11 flex-1 bg-teal-700 hover:bg-teal-800" data-testid="access-submit-btn"><Send className="mr-2 size-4"/>{submitting ? "Sending…" : "Submit request"}</Button>
                </div>
              </div>
            </div>
          )}

          {step === "unknown_domain" && (
            <div data-testid="login-step-unknown-domain">
              <div className="flex items-start gap-2 rounded-md border border-sky-200 bg-sky-50 p-3 text-sm text-sky-900">
                <Building2 className="mt-0.5 size-4" />
                <span>{bannerMessage}</span>
              </div>
              <h3 className="mt-5 font-heading text-lg font-bold">Tell us about your team</h3>
              <p className="mt-1 text-xs text-slate-600">We&apos;ll review your details and reach out to help you get set up on FlowQA.</p>
              <div className="mt-3 space-y-3">
                <div><Label>Full name *</Label><Input value={reqForm.full_name} onChange={(e) => setReqForm({...reqForm, full_name: e.target.value})} data-testid="demo-fullname"/></div>
                <div><Label>Company name *</Label><Input value={reqForm.company_name} onChange={(e) => setReqForm({...reqForm, company_name: e.target.value})} data-testid="demo-company"/></div>
                <div><Label>Role / Title</Label><Input value={reqForm.role_title} onChange={(e) => setReqForm({...reqForm, role_title: e.target.value})} data-testid="demo-role"/></div>
                <div><Label>Phone (optional)</Label><Input value={reqForm.phone} onChange={(e) => setReqForm({...reqForm, phone: e.target.value})} data-testid="demo-phone"/></div>
                <div><Label>Message (optional)</Label><Input value={reqForm.message} onChange={(e) => setReqForm({...reqForm, message: e.target.value})} placeholder="Tell us about your project" data-testid="demo-message"/></div>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => setStep("email")} className="h-11"><ArrowLeft className="mr-1 size-3.5"/>Back</Button>
                  <Button onClick={submitAccessRequest} disabled={submitting} className="h-11 flex-1 bg-teal-700 hover:bg-teal-800" data-testid="demo-submit-btn"><Send className="mr-2 size-4"/>{submitting ? "Sending…" : "Get FlowQA started"}</Button>
                </div>
              </div>
            </div>
          )}

          {step === "submitted" && (
            <div data-testid="login-step-submitted" className="text-center">
              <div className="mx-auto grid size-12 place-items-center rounded-full bg-emerald-50 text-emerald-600"><CheckCircle2 className="size-7" /></div>
              <h3 className="mt-4 font-heading text-2xl font-black tracking-tighter text-slate-900">Request received</h3>
              <p className="mt-2 text-sm text-slate-600">Thanks. We&apos;ve logged your details and will be in touch shortly.</p>
              <Button onClick={() => { setStep("email"); setEmail(""); setReqForm({ full_name: "", company_name: "", phone: "", role_title: "", message: "" }); }} variant="outline" className="mt-5">Back to sign in</Button>
            </div>
          )}

          <p className="mt-6 text-center text-xs text-slate-500">Civil construction QA · ITPs · Hold points · Evidence · Signatures · Audit trails</p>
        </Card>
      </div>
    </div>
  );
}
